import { useEffect, useState } from 'react';
import { MessageBus, Event } from '@podium/browser';

import {
    AdObj,
    AdError,
    Status,
    Format,
    DeviceType,
    DefineTagOptions,
    Size,
    PageType,
    Vertical,
    Subvertical,
} from '../../shared/types/ast';
import { inview } from '../utils/lazy';
import { markAdAvailable, markTagLoaded } from '../utils/performance';

let messageBus: MessageBus;

const devicePrefix = (deviceType: DeviceType) => {
    switch (deviceType) {
        case 'mobile':
            return 'wph';

        case 'tablet':
            return 'wtb';

        case 'desktop':
            return 'wde';
    }
};

function nativeOnly(
    pageType?: PageType,
    vertical?: Vertical,
    subvertical?: Subvertical,
) {
    return (
        vertical === 'travel' &&
        subvertical === 'prepackage' &&
        pageType === 'object'
    );
}

function sizes(
    deviceType: DeviceType,
    pageType?: PageType,
    vertical?: Vertical,
    subvertical?: Subvertical,
    isTakeoverCapable = false,
): Size[] {
    // everyone wants native
    const sizes: Size[] = [[1, 1]];

    if (!nativeOnly(pageType, vertical, subvertical)) {
        if (deviceType === 'mobile') {
            sizes.push([300, 250], [320, 250], [320, 400]);
        } else {
            sizes.push([580, 400]);
        }
    }

    // takeover is temporarily  disabled on travel
    if (isTakeoverCapable && vertical !== 'travel') {
        sizes.push([3, 3], [1920, 1080]);
    }

    return sizes;
}

function formats(
    pageType?: PageType,
    vertical?: Vertical,
    subvertical?: Subvertical,
): Format[] {
    if (nativeOnly(pageType, vertical, subvertical)) {
        return ['native'];
    }
    return ['banner', 'native', 'video'];
}

export function tag(
    deviceType: DeviceType,
    instanceId: string,
    targetId: string,
    pageType?: PageType,
    vertical?: Vertical,
    subvertical?: Subvertical,
): DefineTagOptions | undefined {
    const market = vertical === 'car' ? '_motor' : '';
    switch (instanceId) {
        case 'topboard':
            switch (deviceType) {
                case 'desktop':
                    return {
                        targetId,
                        sizes: [
                            [980, 150],
                            [1010, 150],
                            [728, 90],
                            [928, 120],
                        ],
                        keywords: {
                            'no-sno-adformat': ['topboard'],
                        },
                        invCode: `no-finn-wde-${pageType}${market}_topboard`,
                        allowedFormats: ['banner'],
                    };
                case 'tablet':
                    return {
                        targetId,
                        sizes: [
                            [768, 150],
                            [728, 90],
                        ],
                        keywords: {
                            'no-sno-adformat': ['topboard'],
                        },
                        invCode: `no-finn-wtb-${pageType}${market}_topboard`,
                        allowedFormats: ['banner'],
                    };
                case 'mobile':
                    return;
                default:
                    return;
            }
        case 'skyscraperright_1':
            switch (deviceType) {
                case 'desktop':
                    return {
                        targetId,
                        sizes: [
                            [160, 600],
                            [180, 500],
                            [180, 700],
                            [4, 4],
                            [300, 600],
                        ],

                        keywords: {
                            'no-sno-adformat': ['skyscraperright_1'],
                        },
                        invCode: `no-finn-wde-${pageType}${market}_skyscraperright_1`,
                        allowedFormats: ['banner'],
                    };
                case 'tablet':
                case 'mobile':
                    return;
                default:
                    return;
            }
        case 'wallpaper':
            switch (deviceType) {
                case 'desktop':
                    return {
                        targetId,
                        sizes: [[2, 2]],
                        keywords: {
                            'no-sno-adformat': ['wallpaper'],
                        },
                        invCode: `no-finn-wde-${pageType}${market}_wallpaper`,
                        allowedFormats: ['banner'],
                    };
                case 'tablet':
                case 'mobile':
                    return;
                default:
                    return;
            }
        case 'skyscraperleft_1':
            switch (deviceType) {
                case 'desktop':
                    return {
                        targetId,
                        sizes: [[180, 701]],
                        keywords: {
                            'no-sno-adformat': ['skyscraperleft_1'],
                        },
                        invCode: `no-finn-wde-${pageType}${market}_skyscraperleft_1`,
                        allowedFormats: ['banner'],
                    };
                case 'tablet':
                case 'mobile':
                    return;
                default:
                    return;
            }
        case 'contentboard':
            return {
                targetId,
                sizes: [[1, 1]],
                keywords: {
                    'no-sno-adformat': [instanceId],
                },
                invCode: `no-finn-${devicePrefix(
                    deviceType,
                )}-${pageType}${market}_${instanceId}`,
                allowedFormats: ['native'],
            };
        case 'test_1':
            switch (deviceType) {
                case 'mobile':
                    return {
                        targetId: 'test_1',
                        sizes: [[1, 2]],
                        keywords: { 'no-sno-adformat': ['test_1'] },
                        invCode: `no-finn-wph-${pageType}${market}_test_1`,
                        allowedFormats: ['banner'],
                    };
                case 'tablet':
                    return {
                        targetId: 'test_1',
                        sizes: [[1, 2]],
                        keywords: {
                            'no-sno-adformat': ['test_1'],
                        },
                        invCode: `no-finn-wtb-${pageType}${market}_test_1`,
                        allowedFormats: ['banner'],
                    };
                case 'desktop':
                    return {
                        targetId: 'test_1',
                        sizes: [[1, 2]],
                        keywords: { 'no-sno-adformat': ['test_1'] },
                        invCode: `no-finn-wde-${pageType}${market}_test_1`,
                        allowedFormats: ['banner'],
                    };
                default:
                    return {};
            }
        default:
            return {
                invCode: `no-finn-${devicePrefix(
                    deviceType,
                )}-${pageType}${market}_${instanceId}`,
                sizes: sizes(
                    deviceType,
                    pageType,
                    vertical,
                    subvertical,
                    instanceId === 'board_2',
                ),
                allowedFormats: formats(pageType, vertical, subvertical),
                targetId,
                keywords: {
                    'aa-sch-ssa-format': ['NATIVE_CONTENT'],
                    'no-sno-adformat': [instanceId],
                },
                native: {
                    title: { required: true },
                    clickUrl: { required: true },
                    body: { required: false },
                    description: { required: false },
                    sponsoredBy: { required: false },
                    image: { required: true },
                    icon: { required: false },
                    price: { required: false },
                    cta: { required: false },
                    desc2: { required: false },
                    video: { required: false },
                },
            };
    }
}

const defineTag = (tagConfig?: DefineTagOptions) => {
    if (tagConfig) {
        if (window.apntag && window.apntag.anq) {
            window.apntag.anq.push(() => {
                if (window.apntag) {
                    window.apntag.defineTag(tagConfig);
                }
            });
        }
        return tagConfig;
    }
    return;
};

function initialTargetId(id: string) {
    if (!messageBus) {
        messageBus = new MessageBus();
    }
    const existingCycle = messageBus.peek('banner', 'refresh');
    if (existingCycle) {
        const cycle = existingCycle.payload;
        return id + cycle;
    }
    return id;
}

export function useAd(
    id: string,
    deviceType: DeviceType,
    pageType?: PageType,
    vertical?: Vertical,
    subvertical?: Subvertical,
) {
    const [loaded, setLoaded] = useState(false);
    const [status, setStatus] = useState<Status>('requested');
    const [adObj2, setAdObj] = useState<AdObj>();
    const [error, setError] = useState<AdError>();
    const [targetId, setTargetId] = useState(initialTargetId(id));

    const tagConfig = tag(
        deviceType,
        id,
        targetId,
        pageType,
        vertical,
        subvertical,
    );
    const sizes = tagConfig?.sizes;

    useEffect(() => {
        if (typeof window === 'object') {
            window.FINN = window.FINN || {};
            window.FINN.banner = window.FINN.banner || {};
            window.FINN.banner.queue = window.FINN.banner.queue || [];
            window.FINN.banner.queue.push(() => {
                if (window.apntag) {
                    window.apntag.onEvent('adRequested', targetId, () => {
                        setStatus('requested');
                    });

                    window.apntag.onEvent('adAvailable', targetId, (adObj) => {
                        markAdAvailable(id);
                        setAdObj(adObj);
                        setLoaded(true);
                        setStatus('available');
                    });

                    window.apntag.onEvent('adLoaded', targetId, (adObj) => {
                        setAdObj(adObj);
                        setStatus('loaded');
                    });

                    window.apntag.onEvent('adNoBid', targetId, () => {
                        setLoaded(true);
                        setStatus('failed');
                    });

                    window.apntag.onEvent('adBadRequest', targetId, (error) => {
                        setLoaded(true);
                        setError(error);
                        setStatus('failed');
                    });
                }

                const activeTag = defineTag(tagConfig);
                if (activeTag) {
                    markTagLoaded(id);
                    const { debouncedLoadTags } = window.FINN.banner;
                    if (debouncedLoadTags) {
                        debouncedLoadTags();
                    }
                }
            });
        } else {
            setLoaded(true);
            setStatus('failed');
        }
        if (!messageBus) {
            messageBus = new MessageBus();
        }

        function refreshCallback(event: Event) {
            const cycle = event.payload;
            if (cycle) {
                setTargetId(id + cycle);
            }
        }

        messageBus.subscribe('banner', 'refresh', refreshCallback);
        return function cleanup() {
            setAdObj(undefined);
            if (window.apntag && window.apntag.anq) {
                window.apntag.anq.push(() => {
                    if (window.apntag) {
                        window.apntag.offEvent('adAvailable', targetId);
                        window.apntag.offEvent('adLoaded', targetId);
                        window.apntag.offEvent('adNoBid', targetId);
                        window.apntag.offEvent('adBadRequest', targetId);
                    }
                });
            }
            messageBus.unsubscribe('banner', 'refresh', refreshCallback);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [targetId]); // We only want this to run when targetId changes

    return { adObj: adObj2, loaded, error, status, targetId, sizes };
}

export function useShowTag(id: string, proximity = 100) {
    useEffect(() => {
        const { apntag } = window;
        let cleanup = () => {};
        if (apntag && apntag.anq) {
            const loadElement = (ele: Element) => {
                const tagId = ele.getAttribute('id');
                if (tagId) {
                    apntag.anq.push(() => apntag.showTag(tagId));
                }
            };

            apntag.anq.push(() => {
                const element = document.getElementById(id);
                if (element) {
                    cleanup = inview(element, loadElement, proximity);
                }
            });
        }
        return cleanup;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id]); // We only want this to run when id changes

    return {};
}
